import React, { useState, useCallback, useMemo, useRef } from 'react';
import { AnimatePresence } from 'framer-motion';
import { OnboardingBackground } from './OnboardingBackground';
import { WelcomeStep } from './steps/WelcomeStep';
import { NameStep } from './steps/NameStep';
import { RoleStep } from './steps/RoleStep';
import { GoalsStep } from './steps/GoalsStep';
import { PhaseStep } from './steps/PhaseStep';
import { PriorityStep } from './steps/PriorityStep';
import { BoardGenStep } from './steps/BoardGenStep';
import { HookStep } from './steps/HookStep';
import type { OnboardingData } from '@/stores/profileStore';

type GoalPhase = 'exploring' | 'planned' | 'executing';

interface GoalDraft {
  text: string;
  phase?: GoalPhase;
  priority?: number;
}

type Step =
  | 'welcome'
  | 'name'
  | 'role'
  | 'goals'
  | 'phase'
  | 'priority'
  | 'boardgen'
  | 'hook';

interface MindDumpFlowProps {
  onComplete: () => void;
}

export function MindDumpFlow({ onComplete }: MindDumpFlowProps) {
  const [step, setStep] = useState<Step>('welcome');

  // Form data
  const [name, setName] = useState('');
  const [roles, setRoles] = useState<string[]>([]);
  const [roleOther, setRoleOther] = useState<string | null>(null);
  const [goalTexts, setGoalTexts] = useState<string[]>([]);
  const [goals, setGoals] = useState<GoalDraft[]>([]);
  const [phaseIndex, setPhaseIndex] = useState(0);

  // Use a ref to hold the finalized onboarding data so it doesn't
  // change between BoardGenStep mount and when it reads it.
  const onboardingDataRef = useRef<OnboardingData | null>(null);

  // Transition from goals text input to phase assessment
  const startPhaseAssessment = useCallback(() => {
    const drafts: GoalDraft[] = goalTexts.map((text) => ({ text }));
    setGoals(drafts);
    setPhaseIndex(0);
    if (goalTexts.length > 0) {
      setStep('phase');
    } else {
      // No goals — skip to board gen
      onboardingDataRef.current = {
        name,
        roles,
        roleOther,
        goals: [],
      };
      setStep('boardgen');
    }
  }, [goalTexts, name, roles, roleOther]);

  // Handle phase selection for current goal
  const handlePhaseSelect = useCallback(
    (phase: GoalPhase) => {
      const updatedGoals = goals.map((g, i) =>
        i === phaseIndex ? { ...g, phase } : g
      );

      // Move to next goal (max 3 assessed) or to priority/boardgen
      const maxAssess = Math.min(updatedGoals.length, 3);
      if (phaseIndex < maxAssess - 1) {
        setGoals(updatedGoals);
        setPhaseIndex((prev) => prev + 1);
      } else {
        // Fill in any unassessed goals with default phase
        const finalGoals = updatedGoals.map((g) => ({
          ...g,
          phase: g.phase || 'exploring' as GoalPhase,
        }));
        setGoals(finalGoals);

        if (finalGoals.length >= 2) {
          setStep('priority');
        } else {
          // Single goal — skip priority, go straight to boardgen
          onboardingDataRef.current = {
            name,
            roles,
            roleOther,
            goals: finalGoals.map((g, i) => ({
              text: g.text,
              phase: (g.phase || 'exploring') as GoalPhase,
              priority: i + 1,
            })),
          };
          setStep('boardgen');
        }
      }
    },
    [phaseIndex, goals, name, roles, roleOther]
  );

  // Handle priority ranking completion
  const handlePriorityComplete = useCallback(
    (orderedGoals: Array<{ text: string; phase: string }>) => {
      const finalGoals = orderedGoals.map((g, i) => ({
        text: g.text,
        phase: g.phase as GoalPhase,
        priority: i + 1,
      }));
      setGoals(finalGoals);

      // Freeze the onboarding data into the ref
      onboardingDataRef.current = {
        name,
        roles,
        roleOther,
        goals: finalGoals.map((g, i) => ({
          text: g.text,
          phase: g.phase as GoalPhase,
          priority: g.priority || i + 1,
        })),
      };
      setStep('boardgen');
    },
    [name, roles, roleOther]
  );

  // Goals with phase info for priority step
  const goalsWithPhase = useMemo(
    () =>
      goals.map((g) => ({
        text: g.text,
        phase: g.phase || 'exploring',
      })),
    [goals]
  );

  // Fallback onboarding data (used if ref not set, e.g. 0 goals)
  const onboardingData: OnboardingData = onboardingDataRef.current || {
    name,
    roles,
    roleOther,
    goals: goals.map((g, i) => ({
      text: g.text,
      phase: (g.phase || 'exploring') as GoalPhase,
      priority: g.priority || i + 1,
    })),
  };

  return (
    <OnboardingBackground>
      <AnimatePresence mode="wait">
        {step === 'welcome' && (
          <WelcomeStep key="welcome" onNext={() => setStep('name')} />
        )}

        {step === 'name' && (
          <NameStep
            key="name"
            name={name}
            onNameChange={setName}
            onNext={() => setStep('role')}
          />
        )}

        {step === 'role' && (
          <RoleStep
            key="role"
            name={name}
            roles={roles}
            roleOther={roleOther}
            onRolesChange={setRoles}
            onRoleOtherChange={setRoleOther}
            onNext={() => setStep('goals')}
          />
        )}

        {step === 'goals' && (
          <GoalsStep
            key="goals"
            goals={goalTexts}
            onGoalsChange={setGoalTexts}
            onNext={startPhaseAssessment}
          />
        )}

        {step === 'phase' && goals[phaseIndex] && (
          <PhaseStep
            key={`phase-${phaseIndex}`}
            goalText={goals[phaseIndex].text}
            currentIndex={phaseIndex}
            totalCount={Math.min(goals.length, 3)}
            onSelectPhase={handlePhaseSelect}
          />
        )}

        {step === 'priority' && (
          <PriorityStep
            key="priority"
            goals={goalsWithPhase}
            onComplete={handlePriorityComplete}
          />
        )}

        {step === 'boardgen' && (
          <BoardGenStep
            key="boardgen"
            onboardingData={onboardingData}
            onComplete={() => setStep('hook')}
          />
        )}

        {step === 'hook' && (
          <HookStep key="hook" onComplete={onComplete} />
        )}
      </AnimatePresence>
    </OnboardingBackground>
  );
}
